// SMS Auto Send — the singleton config editor for the cache auto-sender
// (MIS-967 backend, MIS-969 UI; debt-tier split MIS-1554). The page owns the
// WHOLE auto-send config, which since MIS-1553 is TWO independent debt tiers:
//
//   - Over $10K  — the config object's own TOP-LEVEL fields (the exact fields
//     that existed before tiering, so the stored config needed no migration).
//     This is the original block: on/off, send delay (minutes), msgs/min,
//     callback DID, partner allowlist, the four message-copy template groups
//     (openers / openers_no_name / actions / ctas) and the footer.
//   - Under $10K — the IDENTICAL field set nested under `under_10k`: every
//     lead whose debt reads under $10,000, INCLUDING leads with no readable
//     debt at all (~57% of current volume). Ships disabled; enabling it is
//     confirm-gated because flipping it on starts texting roughly 1,700
//     consumers a day.
//
// Both tiers render through one in-file <TierBlock> (the Yes.tsx
// CampaignsEditor {value,onChange,disabled} pattern). The over block keeps its
// original DOM ids ("auto-send-…") and aria-label literals — those strings are
// load-bearing in the test suite — while the under block gets "auto-send-u10-…"
// ids and " — under $10K"-suffixed aria-labels so every id AND aria-label
// stays unique across the two blocks (duplicates break getByLabelText).
//
// The config is delivered in a { config, source } envelope (api.ts returns it
// as-is). We unwrap r.config into the form on load and ALWAYS render the loaded
// values — never blank — so the operator can see the current effective send
// delay/rate/copy (the v2 "can't see the current delay" gap). `source`
// ("stored" | "default") is shown as a Pill so it's obvious whether the values
// are the persisted config or the per-field server defaults.
//
// Save sends the BARE config object (not the envelope) to putAutoSendConfig.
// The admin-api PUT schema is Zod `.strict()` at BOTH levels and `under_10k`
// is REQUIRED — so save composes { ...overTier, under_10k: underTier } from
// exactly the tier fields and nothing else, and validates BOTH tiers with the
// same bounds (errors prefixed with the block name). The response envelope is
// unwrapped the same way so the form reflects exactly what the server stored.
// Styling/token conventions mirror components/SmsSendDelayEditor.tsx +
// pages/SmsCampaignDetail.tsx.

import { useCallback, useEffect, useState } from "react";

import SmsPreview from "../components/SmsPreview";
import {
  api,
  type AutoSendConfig,
  type AutoSendTierConfig,
  type SmsStatsResponse,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

// The cache auto-sender attributes its sends to this reserved pseudo-campaign
// (no row in the campaigns table). The stats route accepts it as a campaignId
// filter; the byCampaign rollup labels it "Form Auto Send".
const FORM_AUTO_SEND_CAMPAIGN_ID = "__form_auto_send__";

// The under-$10K tier attributes to its OWN reserved pseudo-campaign (rtb-api
// sms_dispatch.ts), so its volume never mixes into the over tier's numbers.
const FORM_AUTO_SEND_U10K_CAMPAIGN_ID = "__form_auto_send_u10k__";

// Default lookback for the performance cards — the last 30 days (the stats
// route's max window) so the operator sees a stable cumulative view rather
// than just "today".
const PERF_WINDOW_SEC = 30 * 24 * 3600;

function fmtCount(n: number): string {
  return (n ?? 0).toLocaleString();
}
function fmtUsd(cents: number): string {
  return `$${((cents ?? 0) / 100).toFixed(2)}`;
}

const MAX_DELAY_MS = 86_400_000; // 24 hours
const MIN_DELAY_MS = 60_000; // 1 minute

// 🔴 Confirm copy for enabling the under-$10K tier (MIS-1554). Enabling is the
// dangerous transition — it starts real consumer texting — so it's gated the
// same way Yes.tsx gates entering live-classify mode. Turning OFF stays
// immediate (both existing precedents do this).
const U10K_ENABLE_CONFIRM =
  "Turning this on starts texting under-$10K leads — roughly 1,700 consumers a day at current volume — with this copy from this DID. Turn it on?";

// One tier's editor state — the delay in minutes (operator-facing unit) and
// the four template groups as raw textareas (one variant per line). Everything
// else maps 1:1 onto AutoSendTierConfig. Instantiated twice (over / under).
interface TierFormState {
  enabled: boolean;
  delayMinutes: string; // raw input value (kept as string so empty is allowed)
  msgs_per_min: number;
  callback_did: string;
  openers: string;
  openers_no_name: string;
  actions: string;
  ctas: string;
  footer: string;
  allowed_client_ids: string; // comma/newline-separated client_ids (raw input)
}

// The page edits BOTH debt tiers: `over` maps onto the config's top-level
// fields (the over-$10K tier), `under` onto the nested `under_10k`.
interface FormState {
  over: TierFormState;
  under: TierFormState;
}

// Split a textarea into trimmed, non-empty lines (one template per line).
function toLines(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// Parse the allowed-client_ids input — split on commas OR newlines, trim, drop
// blanks, de-dupe (preserve order). An empty input yields [] ("text nobody").
function toClientIds(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tok of text.split(/[\n,]+/)) {
    const s = tok.trim();
    if (s.length > 0 && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

// Build one tier's form state from its wire config.
function tierToForm(t: AutoSendTierConfig): TierFormState {
  return {
    enabled: t.enabled,
    delayMinutes: String(t.send_delay_ms / 60000),
    msgs_per_min: t.msgs_per_min,
    callback_did: t.callback_did,
    openers: t.openers.join("\n"),
    openers_no_name: t.openers_no_name.join("\n"),
    actions: t.actions.join("\n"),
    ctas: t.ctas.join("\n"),
    footer: t.footer,
    // Show as a comma-separated list (operator-friendly single line). Tolerate a
    // legacy/absent value so the page never crashes on an older stored config.
    allowed_client_ids: (t.allowed_client_ids ?? []).join(", "),
  };
}

// Build the form state from a loaded (or saved) AutoSendConfig envelope body.
// The object's own top-level fields ARE the over tier; `under_10k` is the
// under tier. Defensive: a response from an older backend may omit `under_10k`
// (the same tolerance the page already extends to `allowed_client_ids ?? []`)
// — fall back to the over tier's copy with the tier DOUBLY inert
// (enabled:false + empty allowlist = text nobody), mirroring the server's own
// default posture for the under tier, so an older stored config never crashes
// the page.
function configToForm(c: AutoSendConfig): FormState {
  const under: AutoSendTierConfig = c.under_10k ?? {
    enabled: false,
    send_delay_ms: c.send_delay_ms,
    msgs_per_min: c.msgs_per_min,
    callback_did: c.callback_did,
    openers: c.openers,
    openers_no_name: c.openers_no_name,
    actions: c.actions,
    ctas: c.ctas,
    footer: c.footer,
    allowed_client_ids: [],
  };
  return { over: tierToForm(c), under: tierToForm(under) };
}

// Validate one tier's form and convert it to the wire shape. `label` prefixes
// the error message so the operator knows WHICH block is out of bounds, e.g.
// "Under $10K: Msgs/min must be between 1 and 600." Returns exactly the tier
// fields and nothing else — the PUT schema is strict at both levels.
function tierFromForm(
  label: string,
  t: TierFormState,
):
  | { config: AutoSendTierConfig; error: null }
  | { config: null; error: string } {
  const minutes = Number(t.delayMinutes);
  if (t.delayMinutes.trim() === "" || Number.isNaN(minutes)) {
    return { config: null, error: `${label}: Send delay must be a number of minutes.` };
  }
  const delayMs = Math.round(minutes * 60000);
  if (!(delayMs >= MIN_DELAY_MS && delayMs <= MAX_DELAY_MS)) {
    return {
      config: null,
      error: `${label}: Send delay must be between 1 minute and 24 hours.`,
    };
  }
  if (!(t.msgs_per_min > 0 && t.msgs_per_min <= 600)) {
    return { config: null, error: `${label}: Msgs/min must be between 1 and 600.` };
  }
  return {
    config: {
      enabled: t.enabled,
      send_delay_ms: delayMs,
      msgs_per_min: t.msgs_per_min,
      callback_did: t.callback_did.trim(),
      openers: toLines(t.openers),
      openers_no_name: toLines(t.openers_no_name),
      actions: toLines(t.actions),
      ctas: toLines(t.ctas),
      footer: t.footer.trim(),
      allowed_client_ids: toClientIds(t.allowed_client_ids),
    },
    error: null,
  };
}

export default function SmsAutoSend() {
  const [form, setForm] = useState<FormState | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .getAutoSendConfig()
      .then((r) => {
        // Unwrap the { config, source } envelope — render the bare config.
        // Guard: if the response isn't the expected shape (e.g. an API/edge
        // error fell through to HTML, or a stale backend), show a clean error
        // instead of a cryptic "Cannot read properties of undefined" TypeError.
        if (!r || typeof r.config !== "object" || r.config === null) {
          throw new Error(
            "Unexpected Auto Send response from the server — try reloading.",
          );
        }
        setForm(configToForm(r.config));
        setSource(r.source);
        setError(null);
        setDirty(false);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Failed to load"),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Per-tier form updater — any change marks the form dirty.
  const setTier = useCallback((tier: "over" | "under", next: TierFormState) => {
    setForm((f) => (f ? { ...f, [tier]: next } : f));
    setDirty(true);
  }, []);

  const save = useCallback(() => {
    if (!form) return;

    // The existing bounds checks run PER tier (delay 1min–24h, msgs/min
    // 1–600), each error prefixed with its block name.
    const over = tierFromForm("Over $10K", form.over);
    if (over.config === null) {
      setError(over.error);
      return;
    }
    const under = tierFromForm("Under $10K", form.under);
    if (under.config === null) {
      setError(under.error);
      return;
    }

    // Compose the strict two-level PUT body: the over tier's fields at the top
    // level + the REQUIRED nested under_10k (a PUT without it is a 400).
    const config: AutoSendConfig = { ...over.config, under_10k: under.config };

    setSaving(true);
    api
      .putAutoSendConfig(config)
      .then((r) => {
        // Reflect exactly what the server stored. Guard the shape (see load()).
        if (!r || typeof r.config !== "object" || r.config === null) {
          throw new Error(
            "Saved, but the server returned an unexpected response — reload to confirm.",
          );
        }
        setForm(configToForm(r.config));
        setSource(r.source);
        setError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Save failed"),
      )
      .finally(() => setSaving(false));
  }, [form]);

  return (
    <div>
      <PageHeader
        title="Auto Send"
        subtitle="Form Auto Send — the cache auto-sender's on/off, timing, rate, and message copy, per debt tier (over / under $10K)"
        actions={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            {source && (
              <Pill tone={source === "stored" ? "success" : "neutral"}>{source}</Pill>
            )}
            {savedAt && (
              <span
                style={{ color: tokens.color.tealLight, fontSize: tokens.font.size.xs }}
              >
                Saved{" "}
                {savedAt.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty || !form}
              style={buttonStyle("primary", saving || !dirty || !form)}
            >
              {saving ? "Saving…" : dirty ? "Save" : "Saved"}
            </button>
          </div>
        }
      />

      {error && (
        <Card style={{ marginBottom: tokens.space.lg }}>
          <div
            role="alert"
            style={{ color: tokens.color.danger, fontSize: tokens.font.size.sm }}
          >
            {error}
          </div>
        </Card>
      )}

      {/* Form Auto Send performance — sent / called / converted / revenue for
          the cache auto-sender, scoped to its pseudo-campaign. Self-contained:
          owns its own fetch (separate from the config load/save state). */}
      <FormAutoSendPerformance />

      {/* Under-$10K performance — sends ONLY (calls/conversions/revenue are
          not tracked for this tier by design). */}
      <U10kAutoSendPerformance />

      {loading && !form ? (
        <Card>
          <Skeleton height={20} width={160} />
          <div style={{ height: tokens.space.md }} />
          <Skeleton height={36} />
          <div style={{ height: tokens.space.md }} />
          <Skeleton height={120} />
        </Card>
      ) : form ? (
        <>
          {/* Over $10K — the original block; ids/aria-labels unchanged. The
              section wrappers carry aria-labels so each block is a uniquely
              addressable region (tests scope per-block queries with them). */}
          <section aria-label="Over $10K tier">
            <TierBlock
              idPrefix="auto-send"
              title="Over $10K"
              ariaSuffix=""
              value={form.over}
              onChange={(next) => setTier("over", next)}
              disabled={saving}
            />
          </section>

          <section aria-label="Under $10K tier">
            <p
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                margin: `0 0 ${tokens.space.md}px`,
              }}
            >
              Under $10K = every lead whose debt reads under $10,000 —{" "}
              <strong>including leads whose debt couldn&apos;t be read at all</strong> —
              roughly 57% of current volume. This tier ships disabled and texts
              nothing until it&apos;s turned on here.
            </p>
            <TierBlock
              idPrefix="auto-send-u10"
              title="Under $10K"
              ariaSuffix=" — under $10K"
              value={form.under}
              onChange={(next) => {
                // 🔴 Confirm gate on the off→on transition ONLY (the Yes.tsx
                // live-classify precedent): enabling starts texting real
                // consumers. Turning off stays immediate.
                if (
                  next.enabled &&
                  !form.under.enabled &&
                  !window.confirm(U10K_ENABLE_CONFIRM)
                ) {
                  return;
                }
                setTier("under", next);
              }}
              disabled={saving}
            />
          </section>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form Auto Send performance card — sent / called / converted / revenue for the
// cache auto-sender, fetched from the SMS stats route filtered to the reserved
// "__form_auto_send__" pseudo-campaign. Self-contained: its own fetch +
// loading/error state, so it never blocks the config editor above. Loads once
// on mount over a 30-day window (the route's max).
// ---------------------------------------------------------------------------

function FormAutoSendPerformance() {
  const [totals, setTotals] = useState<SmsStatsResponse["totals"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const end = Math.floor(Date.now() / 1000);
    const start = end - PERF_WINDOW_SEC;
    api
      .smsStats({ start, end, campaignId: FORM_AUTO_SEND_CAMPAIGN_ID })
      .then((s) => {
        if (cancelled) return;
        setTotals(s.totals);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card title="Form Auto Send performance" style={{ marginBottom: tokens.space.xl }}>
      {error && !totals ? (
        <EmptyState title="Couldn’t load performance" hint={error} />
      ) : loading && !totals ? (
        <Skeleton height={88} />
      ) : (
        <>
          {error && (
            <p
              style={{
                margin: `0 0 ${tokens.space.md}px`,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
              }}
            >
              {error} — showing the last successful load.
            </p>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.md,
            }}
          >
            <StatCard label="Sent" value={fmtCount(totals?.delivered ?? 0)} hint="messages delivered (last 30d)" emphasis />
            <StatCard label="Called" value={fmtCount(totals?.attributedCalls ?? 0)} hint="calls after a send" />
            <StatCard label="Converted" value={fmtCount(totals?.conversions ?? 0)} hint="revenue-bearing calls" />
            <StatCard label="Revenue" value={fmtUsd(totals?.revenueCents ?? 0)} hint="attributed revenue" emphasis />
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Under-$10K performance card — SENDS ONLY, by design (MIS-1554). The u10k
// tier produces no attributed calls/conversions/revenue (the stats route
// structurally excludes it from attribution so a u10k send can never steal an
// over-tier call), so this card deliberately shows no call/revenue tiles —
// rendering "$0.00" would misread as "tracked and zero". Clone of
// FormAutoSendPerformance, filtered to the u10k pseudo-campaign.
//
// ⚠️ Data source: `totals` in a u10k-filtered response is structurally EMPTY —
// the route computes totals over TRACKED sends only, which excludes this tier.
// The tier's real volume lives on its own byCampaign row; read that, never
// totals.
// ---------------------------------------------------------------------------

function U10kAutoSendPerformance() {
  const [vol, setVol] = useState<{ sends: number; delivered: number } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const end = Math.floor(Date.now() / 1000);
    const start = end - PERF_WINDOW_SEC;
    api
      .smsStats({ start, end, campaignId: FORM_AUTO_SEND_U10K_CAMPAIGN_ID })
      .then((s) => {
        if (cancelled) return;
        const row = (s.byCampaign ?? []).find(
          (c) => c.campaign_id === FORM_AUTO_SEND_U10K_CAMPAIGN_ID,
        );
        // No row = no sends in the window (the tier ships disabled) — that's a
        // real zero, not an error.
        setVol({ sends: row?.sends ?? 0, delivered: row?.delivered ?? 0 });
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card
      title="Under $10K performance (sends only)"
      style={{ marginBottom: tokens.space.xl }}
    >
      {error && !vol ? (
        <EmptyState title="Couldn’t load performance" hint={error} />
      ) : loading && !vol ? (
        <Skeleton height={88} />
      ) : (
        <>
          {error && (
            <p
              style={{
                margin: `0 0 ${tokens.space.md}px`,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
              }}
            >
              {error} — showing the last successful load.
            </p>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.md,
            }}
          >
            <StatCard label="Sent" value={fmtCount(vol?.delivered ?? 0)} hint="messages delivered (last 30d)" emphasis />
            <StatCard label="Attempted" value={fmtCount(vol?.sends ?? 0)} hint="send attempts, incl. failed/skipped" />
          </div>
          <p
            style={{
              margin: `${tokens.space.md}px 0 0`,
              color: tokens.color.fg2,
              fontSize: tokens.font.size.xs,
            }}
          >
            Sends only — calls, conversions and revenue are not tracked for the
            under-$10K tier by design.
          </p>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TierBlock — ONE debt tier's full editor: the "Sending" card (on/off, delay,
// rate, DID, partner allowlist) + the "Message copy" card (four template
// groups, footer, live preview). Rendered twice — over-$10K (idPrefix
// "auto-send", the original ids) and under-$10K ("auto-send-u10"). In-file
// {value,onChange,disabled} editor — the Yes.tsx CampaignsEditor pattern.
//
// Every DOM id is `${idPrefix}-…` and every aria-label gets `ariaSuffix`
// appended, so the two blocks stay uniquely addressable ("" for the over block
// — its label literals are load-bearing in the test suite).
// ---------------------------------------------------------------------------

function TierBlock({
  idPrefix,
  title,
  ariaSuffix,
  value,
  onChange,
  disabled,
}: {
  idPrefix: string;
  title: string;
  ariaSuffix: string;
  value: TierFormState;
  onChange: (next: TierFormState) => void;
  disabled: boolean;
}) {
  const patch = <K extends keyof TierFormState>(
    key: K,
    v: TierFormState[K],
  ): void => onChange({ ...value, [key]: v });

  return (
    <>
      {/* On/Off + timing + rate + DID */}
      <Card title={`${title} — Sending`} style={{ marginBottom: tokens.space.xl }}>
        <p
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.sm,
            marginTop: 0,
          }}
        >
          When On, qualifying cache leads are texted automatically after the
          send delay (so they have a chance to call in first). When Off, no
          new cache leads are dispatched.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: tokens.space.lg,
            alignItems: "start",
          }}
        >
          {/* On/Off master switch */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: tokens.space.sm }}
          >
            <div
              style={{
                fontSize: tokens.font.size.xs,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tokens.color.fg1,
                fontWeight: 600,
              }}
            >
              Auto send
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
              <span
                style={{
                  color: tokens.color.fg0,
                  fontSize: tokens.font.size.base,
                  fontWeight: 600,
                }}
              >
                SMS Debt Leads Automatically:
              </span>
              <button
                type="button"
                onClick={() => patch("enabled", !value.enabled)}
                disabled={disabled}
                aria-pressed={value.enabled}
                aria-label={`Toggle automatic SMS dispatch${ariaSuffix}`}
                style={buttonStyle(value.enabled ? "primary" : "ghost", disabled)}
              >
                {value.enabled ? "On" : "Off"}
              </button>
            </div>
          </div>

          {/* Send delay (minutes) */}
          <div>
            <FieldLabel
              htmlFor={`${idPrefix}-delay-minutes`}
              hint="minutes to wait after a lead pulls before texting"
            >
              Send delay (minutes)
            </FieldLabel>
            <input
              id={`${idPrefix}-delay-minutes`}
              type="number"
              min="1"
              max="1440"
              value={value.delayMinutes}
              onChange={(e) => patch("delayMinutes", e.target.value)}
              disabled={disabled}
              style={inputStyle}
              aria-label={`Send delay (minutes)${ariaSuffix}`}
            />
          </div>

          {/* Msgs / min */}
          <div>
            <FieldLabel htmlFor={`${idPrefix}-rate`} hint="1–600 (sends per minute)">
              Msgs/min
            </FieldLabel>
            <input
              id={`${idPrefix}-rate`}
              type="number"
              min="1"
              max="600"
              value={value.msgs_per_min}
              onChange={(e) => patch("msgs_per_min", Number(e.target.value))}
              disabled={disabled}
              style={inputStyle}
              aria-label={`Msgs per minute${ariaSuffix}`}
            />
          </div>

          {/* Callback DID */}
          <div>
            <FieldLabel htmlFor={`${idPrefix}-did`}>Callback DID</FieldLabel>
            <input
              id={`${idPrefix}-did`}
              value={value.callback_did}
              onChange={(e) => patch("callback_did", e.target.value)}
              disabled={disabled}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Allowed lead partners (credit-cache client_id allowlist) */}
        <div style={{ marginTop: tokens.space.lg }}>
          <FieldLabel
            htmlFor={`${idPrefix}-client-ids`}
            hint="comma-separated credit-cache client_ids — only these partners' leads are auto-texted (e.g. jim)"
          >
            Allowed lead partners (client_id)
          </FieldLabel>
          <input
            id={`${idPrefix}-client-ids`}
            value={value.allowed_client_ids}
            onChange={(e) => patch("allowed_client_ids", e.target.value)}
            placeholder="jim"
            disabled={disabled}
            style={inputStyle}
            aria-label={`Allowed lead partner client_ids${ariaSuffix}`}
          />
          {toClientIds(value.allowed_client_ids).length === 0 ? (
            <p
              style={{
                margin: `${tokens.space.sm}px 0 0`,
                color: tokens.color.danger,
                fontSize: tokens.font.size.xs,
              }}
            >
              Empty — no leads will be auto-texted (even when Auto send is On).
              Add at least one client_id (e.g. <code>jim</code>) to send.
            </p>
          ) : (
            <p
              style={{
                margin: `${tokens.space.sm}px 0 0`,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.xs,
              }}
            >
              Auto-texting leads from:{" "}
              {toClientIds(value.allowed_client_ids).join(", ")}.
            </p>
          )}
        </div>
      </Card>

      {/* Message copy — the four template groups */}
      <Card title={`${title} — Message copy`} style={{ marginBottom: tokens.space.xl }}>
        <p
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.sm,
            margin: `0 0 ${tokens.space.md}px`,
          }}
        >
          One variant per line. Use{" "}
          <code style={{ fontFamily: tokens.font.mono }}>{"{n}"}</code> for the
          lead&apos;s first name and{" "}
          <code style={{ fontFamily: tokens.font.mono }}>{"{p}"}</code> for the
          callback DID.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: tokens.space.lg,
          }}
        >
          <TemplateField
            id={`${idPrefix}-openers`}
            label="Openers (with name)"
            value={value.openers}
            onChange={(v) => patch("openers", v)}
          />
          <TemplateField
            id={`${idPrefix}-openers-no-name`}
            label="Openers (no name)"
            value={value.openers_no_name}
            onChange={(v) => patch("openers_no_name", v)}
          />
          <TemplateField
            id={`${idPrefix}-actions`}
            label="Actions"
            value={value.actions}
            onChange={(v) => patch("actions", v)}
          />
          <TemplateField
            id={`${idPrefix}-ctas`}
            label="CTAs"
            value={value.ctas}
            onChange={(v) => patch("ctas", v)}
          />
        </div>

        <div style={{ marginTop: tokens.space.lg }}>
          <FieldLabel
            htmlFor={`${idPrefix}-footer`}
            hint="appended to every message (plain text — no {n}/{p})"
          >
            Footer / opt-out suffix
          </FieldLabel>
          <input
            id={`${idPrefix}-footer`}
            value={value.footer}
            maxLength={160}
            onChange={(e) => patch("footer", e.target.value)}
            disabled={disabled}
            style={inputStyle}
          />
        </div>

        {/* Live preview of a real composed message from the current copy. */}
        <SmsPreview
          openers={toLines(value.openers)}
          openers_no_name={toLines(value.openers_no_name)}
          actions={toLines(value.actions)}
          ctas={toLines(value.ctas)}
          callback_did={value.callback_did}
          footer={value.footer}
        />
      </Card>
    </>
  );
}

function TemplateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <textarea
        id={id}
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...inputStyle,
          resize: "vertical",
          fontFamily: tokens.font.mono,
          fontSize: tokens.font.size.sm,
          lineHeight: 1.5,
        }}
      />
    </div>
  );
}
